import type { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { NavigationFlowHandler } from '@adapters/navigation/navigationFlowHandler';
import { ProcessPage } from '@adapters/navigation/page';
import { Component, Inject, OnInit, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import type { Logger } from '@core/model/domain/logger';
import { NotificationSwitcher } from '@core/model/framework/notifications/notificationSwitcher';
import { AbortCase } from '@core/usecase/abortCase';
import { AuthenticateToBankWithCredentials } from '@core/usecase/authenticateToBankWithCredentials';
import { FailCase } from '@core/usecase/failCase';
import { GetAccounts } from '@core/usecase/getAccounts';
import { GetWebauthUrl } from '@core/usecase/getWebauthUrl';
import { SaveWebauthBankConnection } from '@core/usecase/saveWebauthBankConnection';
import {
  browserBroadcastToken,
  isTrackingEnabledToken,
  isWebauthQrCodeEnabledToken,
  loggerToken,
} from '@di/angularProviderMapper';
import { TranslateModule } from '@ngx-translate/core';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { TrackerService } from '@shared/services/tracker.service';
import { GetBankById } from '@core/usecase/getBankById';
import { GoBackToBankChoicePopinComponent } from '@shared/components/go-back-to-bank-choice-popin/go-back-to-bank-choice-popin.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { AccountsAnalysisComponent } from './pages/accounts-analysis/accounts-analysis.component';
import { BankChoiceComponent } from './pages/bank-choice/bank-choice.component';
import { BankConnectionFailureRetryComponent } from './pages/bank-connection-failure-retry/bank-connection-failure-retry.component';
import { WebauthResumedByPartnerComponent } from './pages/webauth-resumed-by-partner/webauth-resumed-by-partner.component';
import { ConsentComponent } from './pages/consent/consent.component';
import { NoneAccountsFoundComponent } from './pages/none-accounts-found/none-accounts-found.component';
import { SelectAccountsComponent } from './pages/select-accounts/select-accounts.component';
import { SelectRegionalBankComponent } from './pages/select-regional-bank/select-regional-bank.component';
import { WaitOnExternalBankLoginComponent } from './pages/wait-on-external-bank-login/wait-on-external-bank-login.component';
import { ProcessVM } from './process.viewmodel';

@Component({
  selector: 'app-process',
  templateUrl: './process.component.html',
  standalone: true,
  imports: [
    ConsentComponent,
    BankChoiceComponent,
    SelectAccountsComponent,
    NoneAccountsFoundComponent,
    WaitOnExternalBankLoginComponent,
    BankConnectionFailureRetryComponent,
    WebauthResumedByPartnerComponent,
    GoBackToBankChoicePopinComponent,
    AccountsAnalysisComponent,
    SelectRegionalBankComponent,
    LoadingComponent,
    TranslateModule,
  ],
})
export class ProcessComponent implements OnInit {
  readonly processVM: ProcessVM;
  private readonly storeService = inject(StoreService);

  constructor(
    private readonly processEventService: ProcessEventService,
    @Inject(loggerToken) private readonly logger: Logger,
    private readonly router: Router,
    private readonly authenticateToBankWithCredentials: AuthenticateToBankWithCredentials,
    private readonly getAccounts: GetAccounts,
    @Inject(browserBroadcastToken) private readonly broadcastService: BrowserBroadcast,
    @Inject(isTrackingEnabledToken) private readonly isTrackingEnabled: boolean,
    private readonly tracker: TrackerService,
    private readonly notificationSwitcher: NotificationSwitcher,
    private readonly navigationFlowHandler: NavigationFlowHandler,
    private readonly saveWebauthBankConnection: SaveWebauthBankConnection,
    private readonly abortCase: AbortCase,
    private readonly failCase: FailCase,
    private readonly getBankById: GetBankById,
    private readonly getWebauthUrl: GetWebauthUrl,
    @Inject(isWebauthQrCodeEnabledToken) private readonly isWebauthQrCodeEnabled: boolean
  ) {
    this.processVM = new ProcessVM(
      this.processEventService,
      this.storeService,
      this.navigationFlowHandler,
      this.broadcastService,
      this.abortCase,
      this.failCase,
      this.logger,
      this.router,
      this.authenticateToBankWithCredentials,
      this.getAccounts,
      this.isTrackingEnabled,
      this.tracker,
      this.notificationSwitcher,
      this.saveWebauthBankConnection,
      this.getBankById,
      this.getWebauthUrl,
      this.isWebauthQrCodeEnabled
    );
  }

  async ngOnInit(): Promise<void> {
    this.processVM.initUserJourney();
  }

  goBack() {
    this.navigationFlowHandler.handleGoBackViaInAppButton();
  }

  readonly isGoBackConfirmationDisplayed = this.storeService.select('IS_GO_BACK_CONFIRMATION_DISPLAYED');

  confirmGoBackToBankChoice() {
    this.navigationFlowHandler.confirmGoBackToBankChoice();
  }

  cancelGoBackToBankChoice() {
    this.navigationFlowHandler.cancelGoBackToBankChoice();
  }

  readonly processPage = this.storeService.select('PROCESS_PAGE');
  readonly shouldDisplayBackButton = computed(() => {
    const processPage = this.processPage();

    if (!processPage) {
      return false;
    }

    const shouldNotDisplayBackButtonPages: ProcessPage[] = [
      'ACCOUNTS_ANALYSIS',
      'LOADING',
      'WEBAUTH_RESUMED_BY_PARTNER',
    ];
    return !shouldNotDisplayBackButtonPages.includes(processPage);
  });

  get title(): string {
    const processPage = this.processPage();

    if (processPage == null) {
      return '';
    }

    switch (processPage) {
      case 'CONSENT':
        return 'consent_title';
      case 'BANK_CHOICE':
        return 'bank_choice_title';
      case 'SELECT_REGIONAL_BANK':
        return 'bank_choice_title';
      case 'WAIT_ON_EXTERNAL_BANK_LOGIN':
        return 'wait_on_external_bank_login_title';
      case 'BANK_CONNECTION_FAILURE_RETRY':
        return 'select_account_title';
      case 'WEBAUTH_RESUMED_BY_PARTNER':
        return 'wait_on_external_bank_login_title';
      case 'SELECT_ACCOUNTS':
        return 'select_account_title';
      case 'NONE_ACCOUNTS':
        return 'select_account_title';
      case 'ACCOUNTS_ANALYSIS':
        return 'connect_to_account_title';
      case 'LOADING':
        return 'loading_title';
      default:
        return this.processVM.neverCase(processPage);
    }
  }
}
